import React, { useState } from "react";
import { motion } from "framer-motion";
import { X, Upload, Check, Loader2 } from "lucide-react";
import { submitRfp } from "../../lib/api";
import { Field, inputStyle, primaryButtonStyle } from "./StepEnergy";

const ALLOWED_EXT = [".pdf", ".csv", ".xlsx", ".png", ".jpg", ".jpeg"];
const MAX_FILES = 5;
const MAX_BYTES = 10 * 1024 * 1024;

/**
 * RFP form drawer — expands in place on Step 3 when the visitor triggers
 * "submit-rfp". Auto-populates from Step 1-3 inputs, requires the four
 * contact fields, accepts up to 5 attachments ≤10 MB each.
 *
 * The "Submit RFP" button IS a button block, deliberately — the ARD calls
 * this out: a form submission is a substantive action, not a nav CTA, so
 * the "no button blocks" rule doesn't apply here.
 */
export default function RfpForm({ values, result, variant = "sb", onClose, onEvent }) {
  const [contact, setContact] = useState({
    name: "",
    org: "",
    role: "",
    email: "",
    phone: "",
  });
  const [files, setFiles] = useState([]);
  const [fileError, setFileError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [confirmation, setConfirmation] = useState(null); // { ref_id, message, received_at }

  const validEmail = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(contact.email);
  const validPhone = /^[0-9+\-\s]{7,15}$/.test(contact.phone);
  const canSubmit =
    contact.name.trim() &&
    contact.org.trim() &&
    validEmail &&
    validPhone &&
    !submitting;

  const onFiles = (e) => {
    const selected = Array.from(e.target.files || []);
    setFileError("");
    if (selected.length > MAX_FILES) {
      setFileError(`Max ${MAX_FILES} attachments.`);
      return;
    }
    for (const f of selected) {
      const ext = f.name.slice(f.name.lastIndexOf(".")).toLowerCase();
      if (!ALLOWED_EXT.includes(ext)) {
        setFileError(`Unsupported file type: ${ext || "(no extension)"}`);
        return;
      }
      if (f.size > MAX_BYTES) {
        setFileError(`"${f.name}" exceeds the 10 MB limit.`);
        return;
      }
    }
    setFiles(selected);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError("");
    try {
      const inputs = {
        ...values,
        system_type: result.systemType,
        system_size_kwh: result.systemSizeKwh,
        payback_years: result.paybackYears === Infinity ? null : result.paybackYears,
        diesel_litres_saved: result.dieselLitresSaved,
        diesel_inr_saved: result.dieselInrSaved,
        landed_before_inr_per_kwh: result.currentLandedInrPerKwh,
        landed_after_inr_per_kwh: result.projectedLandedInrPerKwh,
        tariff_inr_per_kwh: values.tariff,
        dg_hours_per_day: values.dgHours,
        dg_cost_per_litre: values.dgCostPerLitre,
        load_profile: values.loadProfile,
        roof_sqm: values.roofSqm,
        peak_load_kw: values.peakLoadKw,
        existing_solar: values.existingSolar,
      };
      const data = await submitRfp({ contact, variant, inputs, files });
      setConfirmation(data);
      onEvent?.("rfp-submitted", { variant, ref_id: data.ref_id });
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Something went wrong. Please try again.";
      setError(String(detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmation) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        data-testid="rfp-confirmation"
        style={{
          marginTop: 30,
          padding: "28px 28px 32px",
          borderRadius: "var(--radius-lg)",
          background:
            "linear-gradient(180deg, rgba(31,170,89,0.14), rgba(31,170,89,0.02))",
          border: "1px solid rgba(31,170,89,0.4)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 8,
          }}
        >
          <Check size={18} color="var(--green-bright)" />
          <span
            className="gg-eyebrow"
            style={{ color: "var(--green-bright)", margin: 0 }}
          >
            RFP received
          </span>
        </div>
        <h3 className="gg-h2" style={{ marginBottom: 12 }}>
          {confirmation.message}
        </h3>
        <p
          style={{
            fontFamily: "Fraunces, Georgia, serif",
            fontSize: 20,
            color: "var(--gold-soft)",
            marginTop: 8,
          }}
        >
          Reference:{" "}
          <span data-testid="rfp-reference-id">{confirmation.ref_id}</span>
        </p>
        <p style={{ color: "var(--text-dim)", fontSize: 14, marginTop: 6 }}>
          Received {new Date(confirmation.received_at).toLocaleString("en-IN")}.
          Keep this reference for your records.
        </p>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      transition={{ duration: 0.35 }}
      data-testid="rfp-form-drawer"
      style={{ marginTop: 30, overflow: "hidden" }}
    >
      <div
        style={{
          padding: "24px 26px 28px",
          borderRadius: "var(--radius-lg)",
          background:
            "linear-gradient(180deg, rgba(255,255,255,0.05), rgba(255,255,255,0.01))",
          border: "1px solid var(--line-strong)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 12,
            marginBottom: 18,
          }}
        >
          <div>
            <div className="gg-eyebrow" style={{ marginBottom: 6 }}>
              Substantial action
            </div>
            <h3 className="gg-h2">Submit your RFP to Green Gold Engineering.</h3>
            <p
              style={{
                color: "var(--text-dim)",
                fontSize: 14.5,
                lineHeight: 1.6,
                marginTop: 8,
                maxWidth: 640,
              }}
            >
              Your Step 1-3 inputs travel with the RFP so our team lands on
              your data, not a form. Attach a recent bill or a load-data CSV
              if you have one — up to 5 files, 10 MB each.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close RFP form"
            data-testid="rfp-close"
            style={{
              background: "transparent",
              border: "1px solid var(--line)",
              borderRadius: 8,
              width: 30,
              height: 30,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "var(--text-dim)",
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            <X size={14} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: 14,
            }}
          >
            <Field label="Full name*">
              <input
                type="text"
                required
                value={contact.name}
                onChange={(e) => setContact({ ...contact, name: e.target.value })}
                data-testid="rfp-input-name"
                style={inputStyle}
              />
            </Field>
            <Field label="Organisation*">
              <input
                type="text"
                required
                value={contact.org}
                onChange={(e) => setContact({ ...contact, org: e.target.value })}
                data-testid="rfp-input-org"
                style={inputStyle}
              />
            </Field>
            <Field label="Role / title">
              <input
                type="text"
                value={contact.role}
                onChange={(e) => setContact({ ...contact, role: e.target.value })}
                data-testid="rfp-input-role"
                style={inputStyle}
              />
            </Field>
            <Field label="Email*">
              <input
                type="email"
                required
                value={contact.email}
                onChange={(e) => setContact({ ...contact, email: e.target.value })}
                data-testid="rfp-input-email"
                style={{
                  ...inputStyle,
                  borderColor:
                    contact.email && !validEmail
                      ? "rgba(255,120,120,0.5)"
                      : "var(--line)",
                }}
              />
            </Field>
            <Field label="Phone*">
              <input
                type="tel"
                required
                value={contact.phone}
                onChange={(e) => setContact({ ...contact, phone: e.target.value })}
                data-testid="rfp-input-phone"
                style={inputStyle}
              />
            </Field>
          </div>

          <div style={{ marginTop: 22 }}>
            <label
              htmlFor="rfp-file-input"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "10px 14px",
                borderRadius: 10,
                border: "1px dashed var(--line-strong)",
                cursor: "pointer",
                color: "var(--text-dim)",
                fontSize: 14,
              }}
            >
              <Upload size={14} /> Attach up to 5 files (bill, load CSV, site photos)
            </label>
            <input
              id="rfp-file-input"
              type="file"
              multiple
              accept={ALLOWED_EXT.join(",")}
              onChange={onFiles}
              data-testid="rfp-file-input"
              style={{ display: "none" }}
            />
            {files.length > 0 && (
              <ul
                data-testid="rfp-file-list"
                style={{
                  marginTop: 10,
                  paddingLeft: 18,
                  color: "var(--text-cloud)",
                  fontSize: 13,
                  lineHeight: 1.6,
                }}
              >
                {files.map((f) => (
                  <li key={f.name}>
                    {f.name}{" "}
                    <span style={{ color: "var(--text-mute)" }}>
                      · {(f.size / 1024).toFixed(0)} KB
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {fileError && (
              <p
                data-testid="rfp-file-error"
                style={{
                  marginTop: 8,
                  color: "#ff8a8a",
                  fontSize: 13,
                }}
              >
                {fileError}
              </p>
            )}
          </div>

          {error && (
            <p
              data-testid="rfp-submit-error"
              style={{ marginTop: 14, color: "#ff8a8a", fontSize: 13 }}
            >
              {error}
            </p>
          )}

          <div
            style={{
              marginTop: 24,
              display: "flex",
              gap: 12,
              justifyContent: "flex-end",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <span style={{ color: "var(--text-mute)", fontSize: 12 }}>
              Variant: <strong>{variant.toUpperCase()}</strong>
            </span>
            <button
              type="submit"
              disabled={!canSubmit}
              data-testid="rfp-submit"
              style={primaryButtonStyle(!canSubmit)}
            >
              {submitting ? (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <Loader2 size={14} className="animate-spin" /> Submitting…
                </span>
              ) : (
                "Submit RFP"
              )}
            </button>
          </div>
        </form>
      </div>
    </motion.div>
  );
}
